import { useMutation } from '@apollo/client'
import { UPDATE_ORDER_NUMBER, GET_ORDER_BY_ID } from '../OrderQueries'
import { toast } from 'sonner'
import React, { useState } from 'react'
import { Input } from '@/components/ui/input'
import { Pencil, Check, X } from 'lucide-react'

interface EditableOrderNumberProps {
  orderId: string
  currentValue: string | number | null
}

export function EditableOrderNumber({
  orderId,
  currentValue,
}: EditableOrderNumberProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [inputValue, setInputValue] = useState(
    currentValue?.toString() || ''
  )
  const [isUpdating, setIsUpdating] = useState(false)

  const [updateOrderNumber] = useMutation(UPDATE_ORDER_NUMBER, {
    refetchQueries: [
      {
        query: GET_ORDER_BY_ID,
        variables: { id: orderId },
      },
    ],
    onCompleted: () => {
      setIsUpdating(false)
      setIsEditing(false)
      toast.success('Order number updated successfully')
    },
    onError: error => {
      setIsUpdating(false)
      toast.error(`Failed to update order number: ${error.message}`)
    },
  })

  const handleSave = async () => {
    const numValue = inputValue ? parseInt(inputValue, 10) : null
    const currentNumValue = typeof currentValue === 'string'
      ? parseInt(currentValue, 10)
      : currentValue

    if (numValue === currentNumValue) {
      setIsEditing(false)
      return
    }

    if (inputValue && isNaN(numValue!)) {
      toast.error('Please enter a valid number')
      return
    }

    setIsUpdating(true)
    try {
      await updateOrderNumber({
        variables: {
          where: { id: orderId },
          order_number: numValue,
        },
      })
    } catch (error) {
      console.error('Error updating order number:', error)
      setIsUpdating(false)
    }
  }

  const handleCancel = () => {
    setInputValue(currentValue?.toString() || '')
    setIsEditing(false)
  }

  const handleEdit = () => {
    setIsEditing(true)
  }

  if (!isEditing) {
    return (
      <div
        className="group flex items-center gap-2 cursor-pointer"
        onMouseEnter={() => {}}
      >
        <span className="text-xs">{currentValue ?? '-'}</span>
        <button
          onClick={handleEdit}
          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-100 rounded"
          title="Edit order number"
        >
          <Pencil className="w-3 h-3 text-gray-500" />
        </button>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2">
      <Input
        type="number"
        value={inputValue}
        onChange={e => setInputValue(e.target.value)}
        disabled={isUpdating}
        className="h-8 text-xs"
        placeholder=""
        autoFocus
        onKeyDown={e => {
          if (e.key === 'Enter') handleSave()
          if (e.key === 'Escape') handleCancel()
        }}
      />
      <button
        onClick={handleSave}
        disabled={isUpdating}
        className="p-1 hover:bg-green-50 rounded disabled:opacity-50"
        title="Save"
      >
        <Check className="w-4 h-4 text-green-600" />
      </button>
      <button
        onClick={handleCancel}
        disabled={isUpdating}
        className="p-1 hover:bg-red-50 rounded disabled:opacity-50"
        title="Cancel"
      >
        <X className="w-4 h-4 text-red-600" />
      </button>
    </div>
  )
}
